/**
 * Shared data context — persists inventory + branch data across route changes.
 *
 * Without this, navigating between "/" (InventoryDashboard) and "/branches"
 * (BranchOverview) destroys and remounts the component, causing a full refetch.
 * This context lifts the data above the router so both pages share the same
 * cached response. Data is fetched once on login and refreshed only when the
 * user clicks Refresh.
 *
 * Settings and data stay in step (QA P2-1, P2-2). The dealership settings
 * provider below registers a link (`registerSettingsLink`). While it is
 * there, every inventory / committed / branches load also reads
 * GET /api/settings (before and after the data, so a save made meanwhile is
 * noticed and the data fetched once more), and the data lands together with
 * the settings it was computed under:
 * - same version as the settings in use: the data simply lands;
 * - another version (a save in another tab or by another admin): every other
 *   dataset already loaded is fetched again first, then all of it and the new
 *   settings apply in one render ("Updating figures…" meanwhile, the S-8
 *   pattern); the committed list is dropped and reloaded by whoever shows it.
 * The same check runs when the tab becomes visible again (at most every
 * 30 s). A forced inventory reload (Refresh, refreshAll) drops the committed
 * list as well. Without the link (tests, no provider) loads behave as before.
 *
 * The user's own settings (role preset included, fresh QA F-1) follow the
 * same sync points: every load calls the user settings provider's `sync()`
 * (it throttles to 30 s and skips while a change is unsent), a forced
 * inventory reload calls `sync({ force: true })`. The provider handles tab
 * visibility itself.
 */

import React, {
    createContext, useContext, useState, useCallback, useEffect, useRef,
} from 'react';
import { cacheInfoFromResponse } from '../utils/cacheAge';
import { committedPayload } from '../utils/committedUnit';
import apiService from '../services/apiService';
import logger from '../utils/logger';
import { useUserSettings } from './userSettingsValue';

const DataContext = createContext(null);

/** Minimum time between two visibility-triggered settings checks. */
export const SETTINGS_RECHECK_MS = 30000;

// The datasets that are fetched again when the settings change (committed
// units are dropped instead).
const RELOADABLE = ['inventory', 'combined', 'matrix', 'trends'];
const FAILED = Symbol('failed');

const versionOf = (payload) => {
    const v = Number(payload?.version);
    return payload && typeof payload === 'object' && Number.isFinite(v) ? v : null;
};

export function DataProvider({ children }) {
    // Inventory
    const [inventoryData, setInventoryData] = useState(null);
    const [inventoryLoading, setInventoryLoading] = useState(false);
    const [inventoryError, setInventoryError] = useState(null);
    const [cacheInfo, setCacheInfo] = useState(null);
    const inventoryFetched = useRef(false);

    // Committed units ("Include committed units", v2): fetched the first time
    // the toggle is on, then kept until the inventory is refreshed or the
    // settings change (this provider is keyed on the dealership, so a switch
    // starts over). Never mixed into inventoryData.
    const [committedData, setCommittedData] = useState(null);
    const [committedLoading, setCommittedLoading] = useState(false);
    const [committedError, setCommittedError] = useState(null);
    const committedInFlight = useRef(null);

    // Branches
    const [combinedData, setCombinedData] = useState(null);
    const [combinedLoading, setCombinedLoading] = useState(false);
    const [combinedError, setCombinedError] = useState(null);

    const [matrixData, setMatrixData] = useState(null);
    const [matrixLoading, setMatrixLoading] = useState(false);
    // A failed /pricing-matrix must be visible: without listings to compare
    // against, Listing Coverage would report every unit as "Not listed".
    const [matrixError, setMatrixError] = useState(null);

    const [trendsData, setTrendsData] = useState(null);
    const [trendsLoading, setTrendsLoading] = useState(false);
    const [trendsError, setTrendsError] = useState(null);
    const trendsArgs = useRef({});

    // Used stock movements (Branches tab, sales performance). Independent of
    // the dealership settings (the page applies them), so loaded on its own.
    const [movementsData, setMovementsData] = useState(null);
    const [movementsLoading, setMovementsLoading] = useState(false);
    const [movementsError, setMovementsError] = useState(null);

    // What is on screen now, for the async sync code (state is read late).
    const present = useRef({});
    present.current = {
        inventory: Boolean(inventoryData),
        committed: Boolean(committedData),
        combined: Boolean(combinedData),
        matrix: Boolean(matrixData),
        trends: Boolean(trendsData),
    };

    // ── Settings link ────────────────────────────────────────────────────
    // { version(): number|null, apply(payload), setSyncing(bool) }
    const settingsLink = useRef(null);
    const registerSettingsLink = useCallback((link) => {
        settingsLink.current = link;
        return () => { if (settingsLink.current === link) settingsLink.current = null; };
    }, []);
    const lastSettingsCheck = useRef(0);
    const resyncing = useRef(null);

    // The user settings' sync (F-1); a no-op without their provider.
    const { sync: userSync } = useUserSettings();
    const userSyncRef = useRef(userSync);
    userSyncRef.current = userSync;
    const syncUser = useCallback((force) => {
        const run = userSyncRef.current;
        if (typeof run !== 'function') return;
        Promise.resolve(force ? run({ force: true }) : run()).catch(() => {});
    }, []);

    const fetchSettings = useCallback(async () => {
        try {
            const data = await apiService.getSettings();
            return versionOf(data) === null ? null : data;
        } catch (err) {
            return null;
        }
    }, []);

    // Per dataset: fetch, land (success) and fail (after a settings change,
    // when the data computed under the old settings cannot stay).
    const datasets = useRef(null);
    datasets.current = {
        inventory: {
            fetch: () => apiService.getInventory(),
            land: (d) => {
                setInventoryData(d);
                setCacheInfo(cacheInfoFromResponse(d));
                setInventoryError(null);
                inventoryFetched.current = true;
            },
            fail: () => {
                setInventoryData(null);
                inventoryFetched.current = false;
                setInventoryError('Failed to load inventory data. Please try again.');
            },
        },
        committed: {
            // Cleaned once here: no sell-speed risk, signal or opportunity
            // field of a committed unit reaches any view (R2-B1).
            fetch: () => apiService.getCommittedInventory().then(committedPayload),
            land: (d) => { setCommittedData(d); setCommittedError(null); },
            fail: () => { setCommittedData(null); },
        },
        combined: {
            fetch: () => apiService.getBranchCombined(),
            land: (d) => { setCombinedData(d); setCombinedError(null); },
            fail: () => { setCombinedData(null); setCombinedError('Failed to load branch data.'); },
        },
        matrix: {
            fetch: () => apiService.getPricingMatrix(),
            land: (d) => { setMatrixData(d); setMatrixError(null); },
            fail: () => { setMatrixData(null); setMatrixError('Failed to load listing data.'); },
        },
        trends: {
            fetch: () => apiService.getCategoryTrends(trendsArgs.current),
            land: (d) => { setTrendsData(d); setTrendsError(null); },
            fail: () => { setTrendsData(null); setTrendsError('Failed to load category trends data.'); },
        },
    };

    /**
     * One dataset plus the settings it was computed under. Settings are read
     * before and after; if they differ, a save happened meanwhile and the
     * data is fetched once more. `settings` is null without a link or when
     * /api/settings cannot be read (the data then lands as before).
     */
    const fetchVersioned = useCallback(async (key, { force = false } = {}) => {
        syncUser(force);
        const ds = datasets.current[key];
        if (!settingsLink.current) return { data: await ds.fetch(), settings: null };
        const before = fetchSettings();
        let data = await ds.fetch();
        const first = await before;
        let after = await fetchSettings();
        if (first && after && versionOf(first) !== versionOf(after)) {
            data = await ds.fetch();
            after = (await fetchSettings()) || after;
        }
        lastSettingsCheck.current = Date.now();
        return { data, settings: after || first };
    }, [fetchSettings, syncUser]);

    /**
     * New settings: fetch every other loaded dataset again, then land all of
     * it (`held` included) and apply the settings in one render. Committed
     * units are dropped unless held. If the settings move again meanwhile,
     * one more round under the newer version.
     */
    const resync = useCallback(async (target, held = {}) => {
        const link = settingsLink.current;
        if (!link) {
            Object.entries(held).forEach(([k, d]) => datasets.current[k].land(d));
            return;
        }
        if (resyncing.current) {
            await resyncing.current;
            const applied = link.version();
            if (applied === versionOf(target)) {
                Object.entries(held).forEach(([k, d]) => datasets.current[k].land(d));
                return;
            }
        }
        const run = (async () => {
            link.setSyncing(true);
            try {
                let settings = target;
                let fresh = { ...held };
                const wanted = new Set([
                    ...Object.keys(held),
                    ...RELOADABLE.filter((k) => present.current[k]),
                ]);
                for (let round = 0; round < 2; round += 1) {
                    const got = fresh;
                    const keys = [...wanted].filter((k) => !(k in got));
                    // eslint-disable-next-line no-await-in-loop
                    const results = await Promise.all(keys.map((k) => datasets.current[k].fetch()
                        .then((d) => [k, d])
                        .catch((err) => {
                            logger.error(`Failed to reload ${k} for new dealership settings`, err);
                            return [k, FAILED];
                        })));
                    results.forEach(([k, d]) => { got[k] = d; });
                    // eslint-disable-next-line no-await-in-loop
                    const latest = await fetchSettings();
                    if (!latest || versionOf(latest) === versionOf(settings)) break;
                    settings = latest;
                    fresh = {};
                }
                lastSettingsCheck.current = Date.now();
                // One synchronous block: React renders it as one update.
                Object.entries(fresh).forEach(([k, d]) => {
                    if (d === FAILED) datasets.current[k].fail();
                    else datasets.current[k].land(d);
                });
                if (!('committed' in fresh)) setCommittedData(null);
                link.apply(settings);
            } finally {
                link.setSyncing(false);
            }
        })();
        resyncing.current = run;
        try {
            await run;
        } finally {
            if (resyncing.current === run) resyncing.current = null;
        }
    }, [fetchSettings]);

    /** Land a dataset fetched by fetchVersioned, keeping settings in step. */
    const commit = useCallback(async function commitDataset(key, { data, settings }, { dropCommitted = false } = {}) {
        const link = settingsLink.current;
        const applied = link ? link.version() : null;
        const v = versionOf(settings);
        if (!link || v === null || applied === null || v === applied) {
            datasets.current[key].land(data);
            if (dropCommitted) setCommittedData(null);
            if (link && v !== null && applied === null) link.apply(settings);
            return;
        }
        // Computed under settings older than those already in use (it was
        // on its way while they switched): fetch just this dataset again.
        if (v < applied) {
            await commitDataset(key, await fetchVersioned(key));
            return;
        }
        await resync(settings, { [key]: data });
    }, [resync, fetchVersioned]);

    /**
     * A settings payload seen outside a load (the provider's own fetch, the
     * visibility check): applied at once when it matches what is shown or
     * nothing is loaded; a newer version resyncs; an older one is stale.
     */
    const observeSettings = useCallback(async (payload) => {
        const link = settingsLink.current;
        const v = versionOf(payload);
        if (!link || v === null) return;
        const applied = link.version();
        if (applied !== null && v < applied) return;
        const anyLoaded = RELOADABLE.some((k) => present.current[k]) || present.current.committed;
        if (applied === null || v === applied || !anyLoaded) {
            link.apply(payload);
            return;
        }
        await resync(payload);
    }, [resync]);

    // Back to this tab: has someone changed the settings meanwhile?
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible' || !settingsLink.current) return;
            const now = Date.now();
            if (now - lastSettingsCheck.current < SETTINGS_RECHECK_MS) return;
            lastSettingsCheck.current = now;
            fetchSettings().then((payload) => { if (payload) observeSettings(payload); });
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [fetchSettings, observeSettings]);

    // ── Loads ────────────────────────────────────────────────────────────

    const loadInventory = useCallback(async (force = false) => {
        if (inventoryLoading) return;
        if (!force && inventoryFetched.current && inventoryData) return;

        setInventoryLoading(true);
        setInventoryError(null);
        try {
            const result = await fetchVersioned('inventory', { force });
            // A forced reload (Refresh) drops the committed list too (P2-1).
            await commit('inventory', result, { dropCommitted: force });
            return true;
        } catch (error) {
            logger.error('Failed to load inventory', error);
            setInventoryError('Failed to load inventory data. Please try again.');
            return false;
        } finally {
            setInventoryLoading(false);
        }
    }, [inventoryLoading, inventoryData, fetchVersioned, commit]);

    const loadCommitted = useCallback(() => {
        if (committedData) return Promise.resolve(true);
        if (committedInFlight.current) return committedInFlight.current;
        setCommittedLoading(true);
        setCommittedError(null);
        const mine = fetchVersioned('committed')
            .then(async (result) => {
                await commit('committed', result);
                return true;
            })
            .catch((error) => {
                logger.error('Failed to load committed units', error);
                setCommittedError('Committed units could not be loaded.');
                return false;
            })
            .finally(() => {
                if (committedInFlight.current === mine) committedInFlight.current = null;
                setCommittedLoading(false);
            });
        committedInFlight.current = mine;
        return mine;
    }, [committedData, fetchVersioned, commit]);

    const loadCombined = useCallback(async (force = false) => {
        if (combinedLoading) return;
        if (!force && combinedData) return;

        setCombinedLoading(true);
        setCombinedError(null);
        try {
            await commit('combined', await fetchVersioned('combined'));
        } catch (error) {
            logger.error('Failed to load branch combined', error);
            setCombinedError('Failed to load branch data.');
        } finally {
            setCombinedLoading(false);
        }
    }, [combinedLoading, combinedData, fetchVersioned, commit]);

    const loadMatrix = useCallback(async (force = false) => {
        if (matrixLoading) return;
        if (!force && matrixData) return;

        setMatrixLoading(true);
        setMatrixError(null);
        try {
            await commit('matrix', await fetchVersioned('matrix'));
        } catch (error) {
            logger.error('Failed to load pricing matrix', error);
            setMatrixError('Failed to load listing data.');
        } finally {
            setMatrixLoading(false);
        }
    }, [matrixLoading, matrixData, fetchVersioned, commit]);

    const loadTrends = useCallback(async ({ dateFrom, dateTo, force = false } = {}) => {
        if (trendsLoading) return;
        if (!force && trendsData) return;

        setTrendsLoading(true);
        setTrendsError(null);
        trendsArgs.current = { dateFrom, dateTo };
        try {
            await commit('trends', await fetchVersioned('trends'));
        } catch (error) {
            logger.error('Failed to load trends', error);
            setTrendsError('Failed to load category trends data.');
        } finally {
            setTrendsLoading(false);
        }
    }, [trendsLoading, trendsData, fetchVersioned, commit]);

    // One request in flight at a time (QA P2 B10): StrictMode's double effect
    // and two callers in the same tick read stale state, so a ref holds the
    // running promise and every caller shares it. The provider is keyed on the
    // dealership, so this is per tenant.
    const movementsInFlight = useRef(null);
    const loadMovements = useCallback((force = false) => {
        if (movementsInFlight.current) return movementsInFlight.current;
        if (!force && movementsData) return Promise.resolve();

        setMovementsLoading(true);
        setMovementsError(null);
        const run = (async () => {
            try {
                setMovementsData(await apiService.getBranchMovements());
            } catch (error) {
                logger.error('Failed to load branch movements', error);
                setMovementsError('Failed to load sales history.');
            } finally {
                movementsInFlight.current = null;
                setMovementsLoading(false);
            }
        })();
        movementsInFlight.current = run;
        return run;
    }, [movementsData]);

    const refreshAll = useCallback(async () => {
        inventoryFetched.current = false;
        setCombinedData(null);
        setMovementsData(null);
        setMatrixData(null);
        setTrendsData(null);
        present.current = {
            ...present.current, combined: false, matrix: false, trends: false,
        };
        // True / false: whether the fresh inventory arrived (dealership
        // settings wait for it before they apply, QA S-8).
        return loadInventory(true);
    }, [loadInventory]);

    return (
        <DataContext.Provider value={{
            inventoryData, inventoryLoading, inventoryError, cacheInfo,
            loadInventory, refreshAll,
            committedData, committedLoading, committedError, loadCommitted,
            combinedData, combinedLoading, combinedError, loadCombined,
            matrixData, matrixLoading, matrixError, loadMatrix,
            trendsData, trendsLoading, trendsError, loadTrends, setTrendsData,
            movementsData, movementsLoading, movementsError, loadMovements,
            registerSettingsLink, observeSettings,
        }}>
            {children}
        </DataContext.Provider>
    );
}

export function useData() {
    const ctx = useContext(DataContext);
    if (!ctx) throw new Error('useData must be inside DataProvider');
    return ctx;
}

/**
 * Like useData, but null outside a DataProvider — for views where the shared
 * data only adds context (the Task Board's unit details) and must not be a
 * hard requirement.
 */
export function useOptionalData() {
    return useContext(DataContext);
}
